import { ChevronsRight, FileSpreadsheet, Link as LinkIcon } from 'lucide-react';
import { SlideOverPanel, Pill } from '@shared/ui';
import { BRAND_GRADIENT } from '@shared/styles/brand';
import { useSetAccount } from '@shared/state/accountStore';
import type { AccountInfo } from '@features/trades/hooks/useTradeMetadata';
import { useCqgDisconnect } from '@features/cqg/hooks/useCqgConnect';
import { useRithmicDisconnect } from '@features/rithmic/hooks/useRithmicConnect';
import { forgetCqgConnection } from '@features/cqg/components/CqgConnectorDialog';
import { forgetRithmicConnection } from '@features/rithmic/components/RithmicConnectDialog';
import { RithmicAttribution } from '@features/rithmic/components/RithmicAttribution';
import { ACCOUNTS_PANEL_WIDTH_PX, TOPBAR_HEIGHT_PX } from '@shared/components/layout/layoutMetrics';
import { useAccountConnection } from '../hooks/useAccountConnection';
import { useConnectDialogStore } from '../state/connectDialogStore';
import { readAccountLogin } from '../lib/accountLogins';
import { readRithmicAccountMeta } from '@features/rithmic/lib/rithmicAccountMeta';
import type { AccountConnectionInfo } from '../lib/accountConnection';

/**
 * The right-side Accounts selection panel (Figma "Sidepanel trades edition Account", 2026-07
 * redesign): a NON-MODAL 400px drawer under the top bar — the app beside it stays visible and
 * clickable. Replaces the old `AccountDropdown` as THE account selector: clicking a row re-keys
 * every scoped query via the global `accountStore`, exactly as before. Per-row live status joins
 * `useAccounts()` against each provider session's owned-account set (`useAccountConnection`).
 *
 * Row actions: live rows **Unlink** (= the provider's user-intent disconnect: forget creds + latch
 * auto-resume + POST — session-level, so it drops EVERY account of that provider's session);
 * offline broker rows **Link** (opens that provider's connect dialog — also the recovery for an
 * account owned by a different login); imported rows have no action (no connect flow).
 */
export function AccountsPanel() {
  const { accounts, selectedName, forAccount } = useAccountConnection();
  const setAccount = useSetAccount();
  const cqgDisconnect = useCqgDisconnect();
  const rithmicDisconnect = useRithmicDisconnect();
  const store = useConnectDialogStore();

  const rows = accounts.data ?? [];
  // Rithmic attribution gate — see the badge block at the bottom of the panel.
  const hasRithmicAccount = rows.some(a => a.provider === 'rithmic');

  const unlink = (a: AccountInfo) => {
    if (a.provider === 'cqg') { forgetCqgConnection(); cqgDisconnect.mutate(); }
    else if (a.provider === 'rithmic') { forgetRithmicConnection(); rithmicDisconnect.mutate(); }
  };
  const link = (a: AccountInfo) => {
    if (a.provider === 'cqg') {
      // Preselect the Demo/Live tab + prefill the username last used for THIS account (FE-only
      // per-account memory; BE environment is primary, the cookie fills the username the DB never holds).
      const remembered = readAccountLogin(a.name);
      store.openCqg({
        environment: a.environment ?? remembered?.environment ?? undefined,
        username: remembered?.username ?? undefined,
      });
    } else if (a.provider === 'rithmic') {
      // Preselect the system dropdown + prefill the username last used for THIS account (FE-only
      // per-account memory; Rithmic keeps no BE system/username, so the cookie is the only source).
      const remembered = readAccountLogin(a.name);
      store.openRithmic({
        system: remembered?.system ?? undefined,
        username: remembered?.username ?? undefined,
      });
    }
  };

  return (
    <SlideOverPanel
      open={store.panelOpen}
      onClose={store.closePanel}
      ariaLabel="Accounts"
      maxWidthPx={ACCOUNTS_PANEL_WIDTH_PX}
      insetTopPx={TOPBAR_HEIGHT_PX}
      surfaceClassName="bg-canvas"
      showScrim={false}
      square
    >
      <div className="flex h-full flex-col gap-4 p-4">
        {/* Header — left collapse chevrons, centered title, invisible mirror spacer (Figma). */}
        <div className="flex h-6 shrink-0 items-center justify-between">
          <button
            type="button"
            onClick={store.closePanel}
            aria-label="Close accounts panel"
            className="text-text-muted transition-colors hover:text-text"
          >
            <ChevronsRight className="h-6 w-6" strokeWidth={2} />
          </button>
          <h2 className="text-base font-bold leading-[19px] text-text">Accounts</h2>
          <ChevronsRight className="h-6 w-6 opacity-0" aria-hidden />
        </div>

        {/* Rows */}
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
          {accounts.isPending && (
            <div className="px-1 py-2 text-sm text-text-muted">Loading…</div>
          )}

          {!accounts.isPending && rows.length === 0 && (
            <div className="flex flex-col gap-2 rounded-xl bg-canvas-raised p-4 text-[13px] leading-5 text-text-muted">
              <p className="font-semibold text-text">No accounts yet</p>
              <p>Connect a broker account to start syncing your trades automatically.</p>
            </div>
          )}

          {rows.map(a => (
            <AccountRow
              key={a.name}
              account={a}
              selected={a.name === selectedName}
              conn={forAccount(a)}
              onSelect={() => { setAccount(a.name); store.closePanel(); }}
              onLink={() => link(a)}
              onUnlink={() => unlink(a)}
            />
          ))}
        </div>

        {/* Pinned Add-Account CTA (Figma: full-width 32px gradient button, 24px from the bottom), with
            the Rithmic/OMNE attribution beneath it when this panel is showing Rithmic-owned data. */}
        <div className="flex shrink-0 flex-col gap-3 pb-2">
          <button
            type="button"
            onClick={store.openAddAccount}
            className="h-8 w-full rounded-lg text-[13px] font-semibold text-white transition-opacity hover:opacity-90"
            style={{ background: BRAND_GRADIENT }}
          >
            Add Account
          </button>
          {/* Rithmic conformance (review 2026-07-24): the two logos must appear wherever the user is
              logged in to / viewing data originating from Rithmic. This panel prints Rithmic account
              ids + names that came from their ResponseAccountList, so the badges belong here too.
              Gated on a Rithmic row EXISTING (not on it being selected/live): the Rithmic-derived data
              is on screen either way, while a user with no Rithmic account must not see their marks. */}
          {hasRithmicAccount && (
            <div className="border-t pt-3" style={{ borderColor: 'var(--canvas-divider)' }}>
              <RithmicAttribution size="sm" />
            </div>
          )}
        </div>
      </div>
    </SlideOverPanel>
  );
}

const PROVIDER_CHIP: Record<AccountInfo['provider'], string> = {
  cqg: 'CQG',
  rithmic: 'RITHMIC',
  imported: 'IMPORTED',
  manual: 'MANUAL', // filtered out of useAccounts(); kept for exhaustiveness
};

// Real broker marks (official brand icons) for the provider glyph; imported rows fall back to a
// lucide file icon (there is no broker logo for a CSV import).
const BROKER_LOGO: Partial<Record<AccountInfo['provider'], string>> = {
  cqg: '/brokers/cqg.png',
  rithmic: '/brokers/rithmic.png',
};

function ProviderGlyph({ provider }: { provider: AccountInfo['provider'] }) {
  const logo = BROKER_LOGO[provider];
  return (
    <span
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-text-muted"
      style={{ background: 'var(--canvas)' }}
      aria-hidden
    >
      {logo
        ? <img src={logo} alt="" className="h-[18px] w-[18px] object-contain" />
        : <FileSpreadsheet className="h-3.5 w-3.5" />}
    </span>
  );
}

/**
 * One 80px account card (Figma tablerow): glyph · name + provider chip · ID line, and on the right a
 * status chip over the Link/Unlink button. The whole card selects the account; the button doesn't.
 */
function AccountRow({
  account: a, selected, conn, onSelect, onLink, onUnlink,
}: {
  account: AccountInfo;
  selected: boolean;
  conn: AccountConnectionInfo;
  onSelect: () => void;
  onLink: () => void;
  onUnlink: () => void;
}) {
  // Promote the friendly Rithmic account name (from the FE meta store — captured from ResponseAccountList
  // on connect) to the main line when we have it; otherwise the row shows the raw id. The BE keeps no
  // Rithmic display metadata, so the FE store is the only source. User direction 2026-07-22.
  const rithmicName = a.provider === 'rithmic' ? (readRithmicAccountMeta(a.name)?.accountName ?? null) : null;
  const displayName = rithmicName || a.displayName || a.name;
  const idLine = a.brokerageAccountNumber ?? a.name;
  // Show the ID sub-line whenever the main line isn't already that exact id (avoids printing it twice —
  // so a Rithmic row with no captured name, whose main line IS the id, still hides the duplicate).
  const showIdLine = displayName !== idLine;
  // DEMO/LIVE tag for CQG accounts: BE-persisted environment is primary; the FE per-account login
  // memory is a fallback (covers accounts discovered before the BE tracked it / a fresh browser).
  const environment = a.provider === 'cqg' ? (a.environment ?? readAccountLogin(a.name)?.environment ?? null) : null;
  // Rithmic rows also show the username last used to connect this account (the BE keeps only the id,
  // so the FE per-account login memory is the only source). User direction 2026-07-21.
  const rithmicUsername = a.provider === 'rithmic' ? (readAccountLogin(a.name)?.username ?? null) : null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); } }}
      className="flex h-20 shrink-0 cursor-pointer items-end justify-between gap-2 rounded-xl p-3 transition-colors"
      style={selected
        ? { background: 'var(--surface-subtle)', border: '1px solid var(--accent-soft)' }
        : { background: 'var(--canvas-raised)' }}
      aria-pressed={selected}
    >
      <div className="flex h-14 min-w-0 items-center gap-2">
        <ProviderGlyph provider={a.provider} />
        <div className="flex min-w-0 flex-col justify-center gap-1">
          <div className="flex items-center gap-3">
            <span className="truncate text-[13px] font-semibold leading-4 text-text">
              {displayName}
            </span>
            <Pill bg="var(--canvas-divider-strong)" fg="var(--chip-text)">{PROVIDER_CHIP[a.provider]}</Pill>
            {environment && (
              <Pill tone={environment === 'demo' ? 'warning' : 'gain'}>{environment}</Pill>
            )}
          </div>
          {showIdLine && (
            <span className="truncate text-[10px] uppercase leading-3 text-text-muted">ID: {idLine}</span>
          )}
          {rithmicUsername && (
            <span className="truncate text-[10px] leading-3 text-text-muted">{rithmicUsername}</span>
          )}
        </div>
      </div>

      <div className="flex h-14 shrink-0 flex-col items-end justify-between">
        {conn.rowState === 'live' ? (
          <Pill bg="var(--pill-win-bg)" fg="var(--chip-text)">
            <span className="h-2 w-2 rounded-full" style={{ background: 'var(--gain)' }} aria-hidden />
            {selected ? 'Current' : 'Connected'}
          </Pill>
        ) : conn.rowState === 'connecting' ? (
          <Pill tone="accent">Connecting</Pill>
        ) : (
          <Pill tone="neutral">Offline</Pill>
        )}

        {conn.linkable ? (
          /* State-coloured connect action (user direction 2026-07-18): a brand-gradient "Connect"
             when offline (owner, 2026-10-12: "it's not possible to have a positive action in red";
             it was red text), yellow "Connecting" mid-handshake (click → re-opens the provider
             dialog's connecting view), green "Unlink" when live (owner, 2026-10-13; it read
             "Disconnect"; the tooltip still says it disconnects the whole provider session). */
          <button
            type="button"
            onClick={e => { e.stopPropagation(); (conn.isLive ? onUnlink : onLink)(); }}
            title={conn.isLive
              ? `Disconnects the whole ${PROVIDER_CHIP[a.provider]} session`
              : conn.rowState === 'connecting'
                ? `Show the ${PROVIDER_CHIP[a.provider]} connection progress`
                : `Connect ${PROVIDER_CHIP[a.provider]} with the login that owns this account`}
            className="flex h-6 min-w-[48px] items-center justify-center gap-2 rounded-lg px-2 transition-opacity hover:opacity-80"
            style={conn.isLive || conn.rowState === 'connecting'
              ? { background: 'var(--canvas)', color: conn.isLive ? 'var(--gain)' : 'var(--warning)' }
              : { background: BRAND_GRADIENT, color: '#fff' }}
          >
            <LinkIcon className="h-4 w-4" aria-hidden />
            <span className="text-[13px] font-semibold leading-4">
              {conn.isLive ? 'Unlink' : conn.rowState === 'connecting' ? 'Connecting' : 'Connect'}
            </span>
          </button>
        ) : (
          <span className="text-[10px] uppercase leading-3 text-text-subtle">CSV import</span>
        )}
      </div>
    </div>
  );
}
